import {Box, Divider, IconButton, SvgIcon, Toolbar, Tooltip} from '@mui/material'
import MuiAppBar from '@mui/material/AppBar'
import {useTheme} from '@mui/material/styles'
import type {FC, ReactElement} from 'react'
import IngestLagChip from '@containers/Dashboards/LineManagement/components/signals/IngestLagChip'
import AppbarCopyLink from './AppbarCopyLink'
import AppbarProfile from './AppbarProfile'
import AppbarSearch from './AppbarSearch'
import ViewAsBanner from './ViewAsBanner'
import HelpItem from './HelpItem'
import ToggleTheme from './ToggleTheme'
import V2AppSwitch from './V2AppSwitch'
import PageTitle from '@shared/components/PageTitle'
import {useLogo} from '@shared/hooks/useLogo'
import {isRunningInTeams} from '@shared/utils/teamsRuntime'
import AutoAwesomeIcon from 'mdi-react/AutoAwesomeIcon'
import {useCustomTranslation} from '@app/shared/hooks/useCustomTranslation'

interface AppbarProps {
  onToggleDrawer?: () => void
  onToggleAssistant?: () => void
  assistantOpen?: boolean
  isMobile?: boolean
}

const Appbar: FC<AppbarProps> = ({
  onToggleDrawer,
  onToggleAssistant,
  assistantOpen,
  isMobile = false,
}): ReactElement => {
  const theme = useTheme()
  const {mainLogo} = useLogo()
  const [tAlpinist] = useCustomTranslation('alpinist')
  const [tCommon] = useCustomTranslation('common')

  return (
    <MuiAppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: 'background.default',
        zIndex: theme.zIndex.drawer + 1,
        borderBottom: 1,
        borderColor: 'divider',
      }}
      data-testid="mbai-main-appbar"
      id="mbai-main-appbar"
    >
      <Toolbar sx={{minWidth: 0}}>
        {isMobile && onToggleDrawer ? (
          <IconButton
            onClick={onToggleDrawer}
            aria-label={tCommon('navigation.toggle_drawer')}
            sx={{
              mr: 1,
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 0,
            }}
          >
            <img
              src={mainLogo}
              alt="MontBlancAI AppbarMenuToggleLogo"
              style={{
                display: 'block',
                width: '24px',
                height: '24px',
              }}
            />
          </IconButton>
        ) : null}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
          }}
          className="selectorAppbar"
        >
          {/* Page title hidden on mobile */}
          {!isMobile && <PageTitle />}

          {/* Search bar always visible just with difference left padding */}
          {!isMobile && (
            <Box
              sx={{
                mx: 'auto',
                flex: '1 1 auto',
                minWidth: 0,
                pl: isMobile ? 1 : 10,
                pr: isMobile ? 1 : 4,
              }}
            >
              <AppbarSearch />
            </Box>
          )}
          {/* Right section */}
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: {xs: 0.5, sm: 1},
            }}
          >
            <ViewAsBanner />
            {/* Tenant-wide data-freshness / ingest-lag status — quiet green dot
                when healthy, a tinted "Data delayed …" pill when lagging. */}
            {!isMobile && <IngestLagChip />}
            {isRunningInTeams() && <AppbarCopyLink />}
            {!isMobile && <V2AppSwitch />}
            <HelpItem />
            {!isMobile && <ToggleTheme />}
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                backgroundColor: 'text.secondary',
                my: 1,
                ml: 1,
              }}
            />
            <Box sx={{ml: 1}}>
              <AppbarProfile isMobile={isMobile} />
            </Box>
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                backgroundColor: 'text.secondary',
                my: 1,
                ml: 1,
              }}
            />
            {onToggleAssistant ? (
              <Box sx={{ml: 1}}>
                <Tooltip title={tAlpinist('ask_alpinist')} arrow>
                  <IconButton
                    onClick={onToggleAssistant}
                    aria-pressed={Boolean(assistantOpen)}
                    data-testid="mbai-ask-alpinist-button"
                    sx={{
                      backgroundColor: assistantOpen ? 'rgba(143,63,213,0.2)' : 'transparent',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <SvgIcon>
                      <defs>
                        <linearGradient id="mbai-ai-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#0e9afa" />
                          <stop offset="50%" stopColor="#8f3fd5" />
                          <stop offset="100%" stopColor="#e100ff" />
                        </linearGradient>
                      </defs>
                      <AutoAwesomeIcon style={{fill: 'url(#mbai-ai-gradient)'}} />
                    </SvgIcon>
                  </IconButton>
                </Tooltip>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Toolbar>
    </MuiAppBar>
  )
}

export default Appbar
